import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { capture } from '../lib/analytics';

type SubmitState = 'idle' | 'submitting' | 'sent';

export function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [state, setState] = useState<SubmitState>('idle');
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setState('submitting');
    setError(null);
    try {
      await api.submitFeedback({
        message,
        contactEmail: contactEmail.trim() || null,
        pageUrl: window.location.href,
        userAgent: window.navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      });
      setState('sent');
      capture('feedback_submitted');
      setMessage('');
      setContactEmail('');
    } catch (err) {
      setState('idle');
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <div className="fixed right-0 top-1/2 z-40 -translate-y-1/2">
      {open ? (
        <div className="mr-3 w-[min(340px,calc(100vw-32px))] rounded-lg border border-line bg-surface p-4 shadow-cardHover">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold tracking-tight">Report an issue</h2>
              <p className="mt-1 text-xs text-ink-muted">Send a bug, typo, or issue.</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setError(null);
                if (state === 'sent') setState('idle');
              }}
              className="text-lg leading-none text-ink-subtle hover:text-ink"
              aria-label="Close report form"
            >
              x
            </button>
          </div>

          {state === 'sent' ? (
            <div className="mt-4 rounded-lg bg-canvas px-3 py-3 text-sm text-ink">
              Thanks. The report was sent.
            </div>
          ) : (
            <form onSubmit={submit} className="mt-4 space-y-3">
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-ink-subtle">Issue</span>
                <textarea
                  className="input mt-1 min-h-28 resize-y"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  minLength={3}
                  maxLength={3000}
                  required
                  placeholder="What happened?"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-ink-subtle">Email (optional)</span>
                <input
                  className="input mt-1"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </label>
              {error ? <p className="text-xs text-red-600">{error}</p> : null}
              <button
                type="submit"
                className="btn-primary w-full"
                disabled={state === 'submitting'}
              >
                {state === 'submitting' ? 'Sending...' : 'Send report'}
              </button>
            </form>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            capture('feedback_opened');
          }}
          className="flex h-10 w-9 items-center justify-center rounded-l-lg border border-r-0 border-line bg-surface text-ink-subtle shadow-sm transition hover:bg-canvas hover:text-ink-muted"
          aria-label="Report an issue"
          title="Report an issue"
        >
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 17 0Z" />
          </svg>
        </button>
      )}
    </div>
  );
}
